export default class App {
	constructor() {
		this._resizeFunctionsArr = [];
		this._resizeFunctions = {};
		this._resizeFunctionsCounter = 0;
		this._resizeTimeout = null;
		this.resizeThrottle = false;

		this._scrollFunctionsArr = [];
		this._scrollFunctions = {};
		this._scrollFunctionsCounter = 0;
		this._scrollTimeout = null;
		this.scrollThrottle = false;

		this._resizeAnimationPreventerTimeout = 200;

		this.breakpoints = {
			xs: {
				gutter: 10,
				size: 0,
			},
			sm: {
				gutter: 20,
				size: 490,
			},
			md: {
				gutter: 30,
				size: 801,
			},
			lg: {
				gutter: 30,
				size: 1024,
			},
			xl: {
				gutter: 30,
				size: 1440,
			},
		}
	}

	init() {
		this.detectBrowser();
		this.initUploadedFilename();
		this.initCloseCta();
		this.initProductSwitch();
		this.initProductOverviewSwitch();
		this.setEventListeners();
		this.fullHeightListener();
		this.resizeAnimationPreventer();
		this.addNewWindowTitles();
		this.addNoOpener();
		this.addAnchorEnhancement();
		this.wrapCopyright();
		this.initSelectric();
		this.initTippy();
		this.initCopy();
		this.initScrollTo();
		this.initScrollToTop();
		this.initPrintListener();
		this.initLabelClick();
		this.initShowDropdown();
		// this.initAnimatedFormLabels();
		this.initSupportNavToggle();
		this.initLanguageToggle();
		this.initVariantToggle();
		this.initFloatingLabel();
		this.initMobileMenuToggle();
		this.initDownloadSwitch();
		this.initIsVideoIntoView();
		this.initProductSwitchOnUrl();
		this.initSurfaceHeroCheck();
		
		return true;
	}

	setEventListeners() {
		window.addEventListener('resize', this.resize.bind(this), {
			passive: true
		});
		document.addEventListener('scroll', this.scroll.bind(this), {
			passive: true
		});

		document.querySelectorAll('main form label').forEach(label => {
			label.addEventListener('keydown', e => {
				if (e.key.toLowerCase().substr(0, 3) == 'ent') {
					label.click();
				}
			});
		});

		Array.from(document.querySelectorAll('a')).filter(a => !a.hasAttribute('href')).forEach(a => {
			if (!a.hasAttribute('tabindex')) {
				a.setAttribute('tabindex', '0');
			}

			a.addEventListener('keydown', e => {
				if (e.key.toLowerCase().substr(0, 3) == 'ent') {
					a.click();
				}
			});
		});
	}

	resize() {
		const runFunctions = () => {
			for (let i = 0, length = this._resizeFunctionsArr.length; i < length; i++) {
				this._resizeFunctionsArr[i]();
			}
		};
		if (this._resizeTimeout !== null) {
			return;
		}
		runFunctions();
		if (this.resizeThrottle) {
			this._resizeTimeout = setTimeout(() => {
				this._resizeTimeout = null;
				runFunctions();
			}, this.resizeThrottle);
		}
	}

	/**
	 * @param {function} func Function to be called on a resize event
	 */
	registerResizeEvent(func) {
		this._resizeFunctions[this._resizeFunctionsCounter] = func;
		this._resizeFunctionsCounter = this._resizeFunctionsCounter + 1;

		// Rebuild array of events
		this._resizeFunctionsArr = [];
		Object.keys(this._resizeFunctions).forEach(key => {
			this._resizeFunctionsArr.push(this._resizeFunctions[key]);
		});

		return this._resizeFunctionsCounter - 1;
	}

	/**
	 * @param {Number} func Function's ID to be removed from the resize functions
	 */
	unregisterResizeEvent(func) {
		if (this._resizeFunctions[func]) {
			// Found
			delete this._resizeFunctions[func];

			// Rebuild array of events
			this._resizeFunctionsArr = [];
			Object.keys(this._resizeFunctions).forEach(key => {
				this._resizeFunctionsArr.push(this._resizeFunctions[key]);
			});

			return true;
		} else {
			// Not found
			return false;
		}
	}

	/**
	 * @description Fires all registered events for a scroll event in the document
	 */
	scroll() {
		const runFunctions = () => {
			for (let i = 0, length = this._scrollFunctionsArr.length; i < length; i++) {
				this._scrollFunctionsArr[i]();
			}
		};
		if (this._scrollTimeout !== null) {
			return;
		}
		runFunctions();
		if (this.scrollThrottle) {
			this._scrollTimeout = setTimeout(() => {
				this._scrollTimeout = null;
				runFunctions();
			}, this.scrollThrottle);
		}
	}

	/**
	 * @param {function} func Function to be called on a scroll event
	 */
	registerScrollEvent(func) {
		this._scrollFunctions[this._scrollFunctionsCounter] = func;
		this._scrollFunctionsCounter = this._scrollFunctionsCounter + 1;

		// Rebuild array of events
		this._scrollFunctionsArr = [];
		Object.keys(this._scrollFunctions).forEach(key => {
			this._scrollFunctionsArr.push(this._scrollFunctions[key]);
		});

		return this._scrollFunctionsCounter - 1;
	}

	/**
	 * @param {Number} func Function's ID to be removed from the scroll functions
	 */
	unregisterScrollEvent(func) {
		if (this._scrollFunctions[func]) {
			// Found
			delete this._scrollFunctions[func];

			// Rebuild array of events
			this._scrollFunctionsArr = [];
			Object.keys(this._scrollFunctions).forEach(key => {
				this._scrollFunctionsArr.push(this._scrollFunctions[key]);
			});

			return true;
		} else {
			// Not found
			return false;
		}
	}

	/**
	 * @description Adds a 'preventAnimations' class to the html tag on resizing
	 */
	resizeAnimationPreventer() {
		this.resizeTimer = null;
		this.windowWidth = window.innerWidth;
		this.registerResizeEvent(() => {
			if (window.innerWidth !== this.windowWidth) {
				document.documentElement.classList.add('preventAnimations');
				clearTimeout(this.resizeTimer);

				this.resizeTimer = setTimeout(() => {
					document.documentElement.classList.remove('preventAnimations');
				}, this._resizeAnimationPreventerTimeout);

				this.windowWidth = window.innerWidth;
			}
		});
	}

	/**
	 * @description Adds a CSS variable '--fullHeight' to the html tag that creates an 'accurate 100vh'
	 */
	fullHeightListener() {
		const fullHeight = () => {
			document.documentElement.style.setProperty('--fullHeight', `${window.innerHeight}px`);
		};
		this.registerResizeEvent(fullHeight);
		fullHeight();
	}

	/**
	 * @description Adds a browser-specific class to the html tag
	 */
	detectBrowser() {
		// Opera 8.0+
		let isOpera = (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0;

		// Firefox 1.0+
		let isFirefox = typeof InstallTrigger !== 'undefined';

		let isIphone = window.navigator.userAgent.match(/iPhone/i);

		let isIpad = window.navigator.userAgent.match(/iPad/i);

		let isSafari = window.navigator.userAgent.match(/Safari/i) && (window.navigator.userAgent.match(/iPhone/i) || window.navigator.userAgent.match(/iPad/i) || window.navigator.userAgent.match(/macOS/i));

		// Internet Explorer 6-11
		let isIE = /*@cc_on!@*/ false || !!document.documentMode;

		// Edge 20+
		let isEdge = !isIE && !!window.StyleMedia;

		// Chrome 1 - 71
		let isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

		// Blink engine detection
		let isBlink = (isChrome || isOpera) && !!window.CSS;

		if (isOpera) {
			document.documentElement.classList.add('opera');
		}

		if (isFirefox) {
			document.documentElement.classList.add('firefox');
		}

		if (isSafari) {
			document.documentElement.classList.add('safari');
		}

		if (isIphone) {
			document.documentElement.classList.add('iphone');
		}

		if (isIpad) {
			document.documentElement.classList.add('ipad');
		}

		if (isIE) {
			document.documentElement.classList.add('ie');
		}

		if (isEdge) {
			document.documentElement.classList.add('edge');
		}

		if (isChrome) {
			document.documentElement.classList.add('chrome');
		}

		if (isBlink) {
			document.documentElement.classList.add('blink');
		}
	}

	/**
	 * @description Appends text to a tags that tells the user it'll open in a new tab/window. Add a tag with `data-new-window-text` property to change the appended text
	 */
	addNewWindowTitles() {
		let text = '(opent in een nieuw scherm)';
		if (document.querySelector('[data-new-window-text]')) {
			text = document.querySelector('[data-new-window-text]').dataset.newWindowText;
		}

		document.querySelectorAll('a[target="_blank"]').forEach(a => {
			a.setAttribute('title', `${a.getAttribute('title')} ${text}`.trim().replace(/  /g, " "));
		});
	}

	/**
	 * @description Adds the attribute 'rel' set to 'noopener' on external links; This is for security reasons/best practices
	 */
	addNoOpener() {
		document.querySelectorAll('a').forEach(a => {
			if (a.href && !a.hasAttribute('rel')) {
				if (a.hostname != location.hostname) {
					a.setAttribute('rel', 'noopener');
				}
			}
		});
	}

	/**
	 * @description Makes sure anchored tags get a class for it, so offsetting scroll is possible. Also edit anchor links to get the [data-scroll-to] attribute
	 */
	addAnchorEnhancement() {
		// Enhance anchors
		Array.from(document.querySelectorAll('a[id]')).filter(a => !a.href).forEach(a => {
			// Get parent element, add 'anchored' class
			if (a.parentElement) {
				a.parentElement.classList.add('anchored');
			}
		});

		// Enhance anchor links
		Array.from(document.querySelectorAll('a[href]')).filter(a => a.hash && a.hash != '' && a.getAttribute('href').substr(0, 1) == '#').forEach(a => {
			a.dataset.scrollTo = a.hash;
		});
	}

	/**
	 * @description Scroll element into view
	 */
	initScrollTo() {
		document.querySelectorAll('[data-scroll-to]').forEach(el => {
			el.addEventListener('click', e => {
				const target = document.querySelector(el.dataset.scrollTo);

				if (target && target.scrollIntoView) {
					e.preventDefault();
					target.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'start',
					});
				}
			});
		});
	}

	initScrollToTop() {
		this.scrollToTopContainer = document.querySelector('.scrollToTop');

		if (this.scrollToTopContainer) {
			const button = this.scrollToTopContainer.querySelector('button');
			const scroll = () => {
				if (document.documentElement.scrollTop > 400) {
					this.scrollToTopContainer.classList.add('active');
				} else {
					this.scrollToTopContainer.classList.remove('active');
				}
			};

			this.registerScrollEvent(scroll.bind(this));

			button.addEventListener('click', undefined => {
				this.scrollToTopContainer.classList.add('moving');
				button.blur();
				window.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
				setTimeout(() => this.scrollToTopContainer.classList.remove('moving'), 1500);
			});
		}
	}

	wrapCopyright() {
		const containers = [
			document.querySelector('main'),
			document.querySelector('footer'),
		]
		const selector = `a, span, p, li, button, h1, h2, h3, h4, h5, address, blockquote`;

		const replace = element => {
			// Find all instances of �
			if (element.innerHTML && element.innerHTML.indexOf && element.innerHTML.indexOf('©' > -1) && element.innerHTML.indexOf(`<sup>`) < 0) {
				// Unwrapped copyright found
				// const original = element.cloneNode(true);
				element.innerHTML = element.innerHTML.replace(/©/gi, `<sup>©</sup>`);
			}
		}

		containers.forEach(container => {
			if (container) {
				container.querySelectorAll(selector).forEach(element => {
					replace(element);
				});
			}
		});
	}

	initSelectric() {
		if (document.querySelectorAll('main select').length) {
			try {
				import( /* webpackChunkName: "modules/dropdown" */ 'selectric').then(e => {

					document.querySelectorAll('main select').forEach(select => {
						const options = {
							arrowButtonMarkup: `<b class="button"><svg class="sprite icon_chevron_down"><use xlink:href="#icon_chevron_down"></use></svg></b>`
						};
						
						if (select.querySelector('option[data-price')) {
							options.optionsItemBuilder = function(itemData) {
								if (itemData.element.data('price')) {
									return `${itemData.text} <span class="price">${itemData.element.data('price')}</span>`;
								} else {
									return itemData.text;
								}
							};
						}

						if (select.querySelector('option[data-price]')) {
							options.labelBuilder = function(itemData) {
								if (itemData.element.data('price')) {
									return `${itemData.text} <span class="price">${itemData.element.data('price')}</span>`;
								} else {
									return itemData.text;
								}
							}
						}	
						
						$(select).selectric(options);
					});
				});
			} catch (e) {
				console.error(e);
			}
		}
	}

	/**
	 * @description Tooltip library
	 */
	initTippy() {
		if (document.querySelectorAll('[data-tippy-content]').length || document.querySelectorAll('[data-tippy-source]').length) {
			try {
				import( /* webpackChunkName: "modules/tooltip" */ 'tippy.js').then(e => {
					const tippy = e.default;
					tippy('[data-tippy-content]', {
						placement: 'bottom',
						theme: 'light',
					});

					if (document.querySelectorAll('[data-tippy-source]').forEach(source => {
							const target = document.querySelector(`[data-tippy-target="${source.dataset.tippySource}"]`);
							if (target) {
								tippy(source, {
									allowHTML: true,
									content: target.innerHTML,
									interactive: target.querySelectorAll('a, button').length > 0
								});
							}
						}));
				});
			} catch (e) {
				console.error(e);
			}
		}

	}

	/**
	 * @description Makes tags with the data-copy property clickable, copies its content to the clipboard
	 */
	initCopy() {
		document.querySelectorAll('[data-copy]').forEach(el => {
			el.addEventListener('click', () => {
				try {
					const copyContent = el.dataset.copy;
					const input = document.createElement('input');

					input.type = 'text';
					input.style.position = 'absolute';
					input.style.height = 0;
					input.style.overflow = 'hidden';
					input.value = copyContent;

					document.body.appendChild(input);
					input.select();
					document.execCommand('copy');
					input.parentNode.removeChild(input);
				} catch (e) {
					this.toast('Something unexpected went wrong', 'error');
					console.error(e);
				}
			});
		});
	}

	toast(message, type = 'success') {
		if (this.toastController) {
			this.toastController.new(message, type);
		} else {
			console.error('ToastController not available. Message below');
			console.log(`[${type}] ${message}`);
		}
	}

	initPrintListener() {
		this.printTexts = Array.from(document.querySelectorAll('[data-print]'));

		if (this.printTexts.length) {
			let printElements = [];

			window.addEventListener('beforeprint', e => {
				this.printTexts.forEach(printParent => {
					let span = document.createElement('span');
					span.innerHTML = printParent.dataset.print;
					span.classList.add('printText');

					printElements.push(span);

					printParent.appendChild(span);
				});
			});

			window.addEventListener('afterprint', e => {
				printElements.forEach(printElement => {
					printElement.remove();
				});

				printElements = [];
			});
		}
	}

	initLabelClick() {
		document.querySelectorAll('label[for]').forEach(label => {
			label.addEventListener('keydown', e => {

				switch (e.key.toLowerCase()) {
					case ' ':
						e.preventDefault();
					case 'space':
					case 'enter':
						label.click();
						break;
				}
			});
		});
	}

	getCurrentBreakpoint() {
		const width = window.innerWidth;
		const breakpoints = Object.keys(this.breakpoints).reverse();

		let currentBreakpoint = breakpoints[breakpoints.length - 1];

		breakpoints.forEach(breakpoint => {
			if (width <= this.breakpoints[breakpoint].size) {
				currentBreakpoint = breakpoint;
			}
		});

		return currentBreakpoint;
	}

	initCloseCta() {
		$(".js-close").click(function () {
			$(".heroCta").addClass("hideCta");
		});
	}

	initProductSwitch() {
		if (document.querySelectorAll('#js-productswitchbutton').length) {
			var button = document.getElementById('js-productswitchbutton');
			var switchProduct = document.getElementsByClassName('productSwitchContainer')[0];

			button.addEventListener('click', function onClick() {
				switchProduct.classList.toggle('ir');
				switchProduct.classList.toggle('uv');
			}, false);
		}
	}

	initProductOverviewSwitch() {
		if (document.querySelectorAll('.productSwitchButton').length) {
			var buttons = document.querySelectorAll('.js-productswitchbutton');
			var switchProduct = document.getElementsByClassName('productSwitchContainer')[0];

			buttons.forEach(button => {
				button.addEventListener('click', function onClick() {
					if (button.classList.contains('ir')){
						switchProduct.classList.add('uv');
						switchProduct.classList.remove('ir');
					} else {
						switchProduct.classList.add('ir');
						switchProduct.classList.remove('uv');
					}
				}, false);
			});
		}
	}

	initProductSwitchOnUrl() {
		if (document.querySelectorAll('.productSwitchContainer').length) {
			$(function () {
				if (window.location.hash) {
					var hash = window.location.hash.substring(1);
					var switchProduct = document.getElementsByClassName('productSwitchContainer')[0];

					if (hash == "ir") {
						switchProduct.classList.remove('uv');
						switchProduct.classList.add('ir');
					}

					if (hash == "uv") {
						switchProduct.classList.remove('ir');
						switchProduct.classList.add('uv');
					}
				}

			});
		}
	}

	initSurfaceHeroCheck() {
		if (document.querySelector(`.heroSurface`) && document.body.classList.contains(`light`)) {
			document.body.classList.add(`dark`);
			document.body.classList.remove(`light`);
		}
	}

	initShowDropdown() {
		// $(".ddlLabel").on('click', function () {
		// 	$(this).addClass("show");
		// });

		$(".ddlLabel").on('keydown', function (e) {
			$(this).toggleClass("show");
			e.stopPropagation();
		});

		$(".ddlLabel").on('click', function (e) {
			$(this).toggleClass("show");
			e.stopPropagation();
		});

		// $(".dropdown").mouseleave(function () {
		// 	$(".ddlLabel").removeClass("show");
		// });

		$(document).on("click", function (e) {
			$(".ddlLabel").removeClass("show");
			e.stopPropagation();
		});

	}

	// initAnimatedFormLabels() {
	// 	const inputs = document.querySelectorAll('.dynamicInputRow input,.dynamicInputRow textarea');

	// 	inputs.forEach(input => {
	// 		input.addEventListener('blur', (e) => {
	// 			if (e.target.value.length) {
	// 				e.target.classList.add("full");
	// 			} else {
	// 				e.target.classList.remove("full");
	// 			}
	// 		});
	// 	})

	// 	document.addEventListener('DOMContentLoaded', function() {
			
	// 	 }, false);
	// }

	initFloatingLabel() {
		const shouldFloat = (input) => {
			let float = true;
			if (!input.id) {
				float = false;
			} else if (!document.querySelector(`label[for="${input.id}"]`)) {
				float = false;
			} else if (input.type && ['file', 'checkbox', 'radio'].includes(input.type)) {
				float = false;
			}

			return float;
		}
		document.querySelectorAll('.dynamicInputRow input,.dynamicInputRow textarea').forEach(input => {
			// Check if there's an ID to link a label and there's a label to float
			if (shouldFloat(input)) {
				const label = document.querySelector(`label[for="${input.id}"]`);
				const checkFunc = () => {
					if (input.value.length > 0) {
						input.classList.add('full');
					} else {
						input.classList.remove('full');
					}
				}

				['keypress', 'keydown', 'input', 'blur', 'focus', 'click', 'mouseleave', 'paste', 'cut'].forEach(event => {
					input.addEventListener(event, checkFunc);
				});
				input.addEventListener('blur', () => input.classList.remove('inputHasFocus'));
				input.addEventListener('focus', () => input.classList.add('inputHasFocus'));
				checkFunc();
				label.classList.add('floating');
			};
		});

		// Datepickerfields work a little different, they fill shadow inputs with values
		if (document.querySelectorAll('.dynamicInputRow input.datepickerfieldshadow').length) {
			document.querySelectorAll('.dynamicInputRow input.datepickerfieldshadow').forEach(datepickerinput => {
				const checkValue = () => {
					if (datepickerinput.value.length > 0) {
						datepickerinput.nextElementSibling.classList.add('full');
					} else {
						datepickerinput.nextElementSibling.classList.remove('full');
					};
				}

				let input = datepickerinput.nextElementSibling;
				['keypress', 'keydown', 'input', 'blur', 'focus', 'click', 'mouseleave', 'paste', 'cut'].forEach(event => {
					input.addEventListener(event, checkValue);
				});
			})
		};
	}

	initUploadedFilename() {
		document.querySelectorAll('.upload').forEach(upload => {
			var input = upload.querySelector('input[type="file"]');
			var infoArea = upload.querySelector('span.uploadedFilename');

			input.addEventListener('change', showFileName);

			function showFileName(e) {
				var input = e.target;
				var fileName = input.files[0].name;

				infoArea.innerHTML = '<p class="smallBody"> Uw bestand: ' + fileName + '</p>';
			};

		})
	}

	initSupportNavToggle() {
		document.querySelectorAll('.supportNavToggleContainer').forEach(toggle => {
			var button = toggle.getElementsByClassName('supportNavToggle')[0];

			button.addEventListener('click', function onClick() {
				button.classList.toggle('show');
			}, false);
		})
	}

	initLanguageToggle() {
		if (document.querySelectorAll('.languageSelection').length) {
			var button = document.getElementsByClassName('languageToggle')[0];
			var container = document.getElementsByClassName('languageSelection')[0];

			button.addEventListener('click', function onClick() {
				button.classList.toggle('show');
				container.classList.toggle('show');
			}, false);

			$(document).on("click", function (e) {
				if ($(e.target).closest('.languageToggle').length > 0) {
					return false;
				}
				button.classList.remove("show");
				container.classList.remove("show");
				e.stopPropagation();
			});
		}
	}

	isInView(element) {
		const elPosition = element.getBoundingClientRect();
		const windowHeight = window.innerHeight || document.documentElement.clientHeight;
		const windowWidth = window.innerWidth || document.documentElement.clientWidth;
		if (
			elPosition.top >= 0 &&
			elPosition.bottom <= windowHeight
		) {
			return true;
		} else {

			return false;
		}
	}

	// initIsBGTitleIntoView() {
	// 	const elements = document.querySelectorAll('.bg-title-1,.bg-title-2,.bg-title-3,.bg-title-4,.bg-title-5');

	// 	//delayed effect for elements in view after reload page
	// 	elements.forEach(element => {

	// 		if (this.isInView(element)) {
	// 			setTimeout(function () {
	// 				element.classList.add('inView');
	// 			}, 500)
	// 		}

	// 	});

	// 	//add inView class when in view after scroll
	// 	this.registerScrollEvent(() => {
	// 		elements.forEach(element => {

	// 			if (this.isInView(element)) {
	// 				element.classList.add('inView');
	// 			}
	// 		})
	// 	});

	// }

	productSelector

	initIsVideoIntoView() {
		let elements = document.querySelectorAll('video');
		elements = Array.from(elements).filter(element => {
			if (element.closest('.heroSurface')) {
				return false;
			}
			return true;
		});

		//play/pause when in view after scroll
		this.registerScrollEvent(() => {
			elements.forEach(element => {

				if (this.isInView(element)) {
					element.play();
				} else {
					element.pause();
				}
			})
		});

	}

	initMobileMenuToggle() {
		if (document.querySelectorAll('.mobileMenuButton').length) {
			var button = document.getElementsByClassName('mobileMenuButton')[0];

			button.addEventListener('click', function onClick() {
				button.classList.toggle('active');
			}, false);
		}
	}

	initDownloadSwitch() {
		if (document.querySelectorAll('.downloadSwitchContainer').length) {
			var buttonProducts = document.getElementsByClassName('js-downloadswitchbuttonProducts')[0];
			var buttonSalestools = document.getElementsByClassName('js-downloadswitchbuttonSalestools')[0];
			var downloadProducts = document.getElementsByClassName('productDownloads')[0];
			var downloadSalestools = document.getElementsByClassName('salesDownloads')[0];

			buttonProducts.addEventListener('click', function onClick() {
				console.log('klik');
				buttonSalestools.classList.remove('active');
				buttonProducts.classList.add('active');
				downloadSalestools.classList.remove('active');
				downloadProducts.classList.add('active');
			}, false);

			buttonSalestools.addEventListener('click', function onClick() {
				console.log('klik');
				buttonProducts.classList.remove('active');
				buttonSalestools.classList.add('active');
				downloadProducts.classList.remove('active');
				downloadSalestools.classList.add('active');
			}, false);
		}
	}

	initVariantToggle() {
		if (document.querySelectorAll('.variantSelection').length) {
			var button = document.getElementsByClassName('variantToggle')[0];
			var container = document.getElementsByClassName('variantSelection')[0];

			button.addEventListener('click', function onClick() {
				button.classList.toggle('show');
				container.classList.toggle('show');
			}, false);

			$(document).on("click", function (e) {
				if ($(e.target).closest('.variantToggle').length > 0) {
					return false;
				}
				button.classList.remove("show");
				container.classList.remove("show");
				e.stopPropagation();
			});
		}
	}

}